import React from 'react';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import Button from '../../components/Button';
import { Box, Slider } from '@mui/material';

export default function AudioToPlay({
    speakAloud,
}) {
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [error, setError] = React.useState(null);
    const [isAudioEnded, setIsAudioEnded] = React.useState(false);

    const audioUrl = speakAloud?.speakAloudPayload[0]?.audio;
    const content = speakAloud?.speakAloudPayload[0]?.sentance;
    const audioRef = React.useRef(null);
    const durationResolved = React.useRef(false);

    React.useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        durationResolved.current = false;

        // For WebM blobs recorded by MediaRecorder, the browser often reports
        // Infinity for duration because the file has no seek cues. The workaround
        // is to seek to a huge value — the browser resolves the real end position,
        // fires durationchange with the correct value, then we seek back to 0.
        const handleLoadedMetadata = () => {
            if (audio.duration === Infinity || isNaN(audio.duration)) {
                audio.currentTime = 1e101;
            } else if (audio.duration > 0) {
                setDuration(audio.duration);
                durationResolved.current = true;
            }
        };

        const handleDurationChange = () => {
            if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration);
                durationResolved.current = true;
                if (audio.currentTime > audio.duration - 0.1) {
                    audio.currentTime = 0;
                }
            }
        };

        const handleTimeUpdate = () => {
            if (!durationResolved.current) return;
            const time = audio.currentTime;
            if (time != null) setCurrentTime(time);
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('durationchange', handleDurationChange);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('error', () => setError('Error loading audio. Please try again.'));

        if (audio.readyState >= 1) handleLoadedMetadata();

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('durationchange', handleDurationChange);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [audioUrl]);

    const togglePlayPause = () => {
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
        // setHasPlayedOnce(true);
        setIsAudioEnded(false);
    }

    const handlePause = () => setIsPlaying(false);

    const handleEnd = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        setIsAudioEnded(true);
    };

    const handleReplay = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleSliderChange = (event, newValue) => {
        if (!audioRef.current || !duration) return;
        setCurrentTime(newValue);
        audioRef.current.currentTime = newValue;
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <React.Fragment>
            <Box style={styles.containerSentence}>
                <div>
                    {content && (
                        <div>
                            <span dangerouslySetInnerHTML={{ __html: content }} />
                        </div>
                    )}
                </div>
            </Box>
            <Box style={styles.containerAudio}>
                {/* <Button
                    endIcon={isPlaying ? <PauseCircleIcon /> : <PlayCircleIcon />}
                    size={'small'}
                    type={'secondary'}
                    placeholder={isPlaying ? 'Pause' : 'Play'}
                    onClick={togglePlayPause}
                /> */}
                <div style={styles.speakerContainer}>
                    <VolumeUpIcon style={isPlaying ? styles.speakerPlaying : styles.speaker} />
                    {isPlaying && <div className="speaker-animation" style={styles.speakerAnimation}></div>}
                </div>
            </Box>

            {/* Progress Bar and Time Display */}
            <Box style={styles.progressContainer}>
                <Slider
                    value={currentTime}
                    min={0}
                    max={duration}
                    onChange={handleSliderChange}
                    aria-labelledby="continuous-slider"
                />
                <Box style={styles.timeContainer}>
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                </Box>
            </Box>

            {error && <div style={styles.errorContainer}>{error}</div>}

            <audio
                ref={audioRef}
                src={audioUrl}
                preload="auto"
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnd}
                onError={() => setError('Audio failed to load. Please check the URL or network connection.')}
            />


            <div style={styles.replayBtnContainer}>
                {isAudioEnded ?
                    <Button
                        size={'small'}
                        type={'secondary'}
                        placeholder={
                            <React.Fragment>
                                Replay
                                <ReplayIcon />
                            </React.Fragment>
                        }
                        onClick={handleReplay}
                    />
                    :
                    <Button
                        endIcon={isPlaying ? <PauseCircleIcon /> : <PlayCircleIcon />}
                        size={'small'}
                        type={'secondary'}
                        placeholder={isPlaying ? 'Pause' : 'Play'}
                        onClick={togglePlayPause}
                    />
                }
            </div>

            <style jsx>{`
                @keyframes echo {
                    0% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    100% {
                        transform: scale(2);
                        opacity: 0;
                    }
                }
                .speaker-animation {
                    position: absolute;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: rgba(0, 0, 0, 0.1);
                    animation: echo 1.5s infinite;
                }
            `}</style>
        </React.Fragment>
    );
}

const styles = {
    container: {
        textAlign: 'left',
    },
    containerSentence: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '20px'
    },
    containerAudio: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '10px'
    },
    progressContainer: {
        marginTop: '10px',
        width: '80%',
        margin: '0 auto',
    },
    timeContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
        marginTop: '5px',
    },
    errorContainer: {
        color: 'red',
        textAlign: 'center',
        marginTop: '10px',
    },
    button: {
        marginLeft: '10px',
        background: 'transparent',
        fontWeight: 'bold',
        position: 'absolute',
        top: '10px',
        right: '20px'
    },
    text: {
        marginTop: 'auto',
        marginBottom: 'auto',
        lineHeight: '25px',
        maxWidth: '80%'
    },
    hearButtonContainer: {
        display: 'flex',
        alignItems: 'start'
    },
    replayBtnContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    },
    speakerContainer: {
        position: 'relative',
        width: '40px',
        height: '40px',
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    speaker: {
        fontSize: '40px',
        color: '#333',
    },
    speakerPlaying: {
        fontSize: '40px',
        color: '#333',
        animation: 'echo 1.5s infinite'
    },
    speakerAnimation: {
        position: 'absolute',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        animation: 'echo 1.5s infinite',
    }
};
